import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Modal, Table, Input, Button, Space, Typography, Empty, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { RobotOutlined, SearchOutlined } from '@ant-design/icons';
import * as botService from '@/services/api/bot.service';
import type { Bot } from '@/services/api/bot.service';
import { useAuthStore } from '@/store/auth.store';
import {
  useAgentSelectedBotStore,
  isAgentBotSelectionValid,
  type AgentWorkspaceBotPick,
} from '@/store/agentSelectedBot.store';
import './AgentBotWorkspaceOverlay.css';

const { Text, Paragraph } = Typography;

function pickFromBot(b: Bot): AgentWorkspaceBotPick {
  return { id: b.id, bot_id: b.bot_id, name: b.name };
}

/**
 * Modal + bootstrap: persisted last bot in localStorage (zustand persist), optional picker,
 * default first bot when the agent navigates without a valid selection.
 */
export default function AgentBotWorkspaceOverlay() {
  const user = useAuthStore((s) => s.user);
  const isAgent = user?.role?.toLowerCase() === 'agent';
  const location = useLocation();
  const navigate = useNavigate();

  const workspaceEpoch = useAgentSelectedBotStore((s) => s.workspaceEpoch);
  const pickerOpen = useAgentSelectedBotStore((s) => s.pickerOpen);
  const availableBots = useAgentSelectedBotStore((s) => s.availableBots);
  const pendingReturnPath = useAgentSelectedBotStore((s) => s.pendingReturnPath);

  const openPicker = useAgentSelectedBotStore((s) => s.openPicker);
  const closePicker = useAgentSelectedBotStore((s) => s.closePicker);
  const setSelection = useAgentSelectedBotStore((s) => s.setSelection);
  const setPendingReturnPath = useAgentSelectedBotStore((s) => s.setPendingReturnPath);
  const clearPendingReturnPath = useAgentSelectedBotStore((s) => s.clearPendingReturnPath);

  const [search, setSearch] = useState('');
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [loadingBots, setLoadingBots] = useState(false);

  const bootstrapRan = useRef(false);
  const lastBootstrapUserId = useRef<string | undefined>(undefined);
  const prevWorkspaceEpoch = useRef(workspaceEpoch);
  const skipPathAuto = useRef(true);
  const agentBotIdsKey = user?.agentBotIds?.join('\0') ?? '';

  /** ?pickBot=1 opens picker; optional returnTo= */
  useEffect(() => {
    if (!isAgent) return;
    const q = new URLSearchParams(location.search);
    if (q.get('pickBot') !== '1') return;
    const rt = q.get('returnTo');
    if (rt && rt.startsWith('/') && !rt.startsWith('//')) {
      try {
        const decoded = decodeURIComponent(rt);
        if (decoded.startsWith('/') && !decoded.startsWith('//')) {
          setPendingReturnPath(decoded);
        }
      } catch {
        /* ignore */
      }
    }
    openPicker();
    q.delete('pickBot');
    q.delete('returnTo');
    const next = q.toString();
    navigate({ pathname: location.pathname, search: next ? `?${next}` : '' }, { replace: true });
  }, [isAgent, location.search, location.pathname, navigate, openPicker, setPendingReturnPath]);

  /** After persist rehydrate: validate selection, load bots, single-bot auto or open modal. */
  useEffect(() => {
    if (!isAgent) {
      bootstrapRan.current = false;
      lastBootstrapUserId.current = undefined;
      return;
    }

    if (lastBootstrapUserId.current !== user?.id) {
      bootstrapRan.current = false;
      lastBootstrapUserId.current = user?.id;
    }

    if (prevWorkspaceEpoch.current !== workspaceEpoch) {
      bootstrapRan.current = false;
      prevWorkspaceEpoch.current = workspaceEpoch;
    }

    let cancelled = false;

    const run = async () => {
      if (bootstrapRan.current) return;
      const u = useAuthStore.getState().user;
      const st = useAgentSelectedBotStore.getState();
      if (!u || u.role?.toLowerCase() !== 'agent') return;
      if (isAgentBotSelectionValid(u, st.selection)) {
        bootstrapRan.current = true;
        return;
      }

      setLoadingBots(true);
      try {
        const res = await botService.listBots({ limit: 200, page: 1 });
        if (cancelled) return;
        const bots = res.bots || [];
        useAgentSelectedBotStore.getState().setAvailableBots(bots);
        if (bots.length === 1) {
          useAgentSelectedBotStore.getState().setSelection(pickFromBot(bots[0]));
          return;
        }
        useAgentSelectedBotStore.getState().openPicker();
      } catch {
        if (!cancelled) {
          useAgentSelectedBotStore.getState().setAvailableBots([]);
          useAgentSelectedBotStore.getState().openPicker();
        }
      } finally {
        setLoadingBots(false);
        if (!cancelled) {
          bootstrapRan.current = true;
        }
      }
    };

    if (useAgentSelectedBotStore.persist.hasHydrated()) {
      void run();
      return () => {
        cancelled = true;
      };
    }

    const unsub = useAgentSelectedBotStore.persist.onFinishHydration(() => {
      void run();
    });
    return () => {
      cancelled = true;
      unsub();
    };
  }, [isAgent, user?.id, workspaceEpoch, agentBotIdsKey]);

  /** If agent navigates without a valid bot, default to first available bot. */
  useEffect(() => {
    if (!isAgent) return;
    if (skipPathAuto.current) {
      skipPathAuto.current = false;
      return;
    }
    const u = useAuthStore.getState().user;
    const st = useAgentSelectedBotStore.getState();
    if (isAgentBotSelectionValid(u, st.selection)) return;
    const bots = st.availableBots;
    if (bots.length === 0) return;
    useAgentSelectedBotStore.getState().setSelection(pickFromBot(bots[0]));
  }, [location.pathname, isAgent]);

  const filteredBots = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return availableBots;
    return availableBots.filter(
      (b) =>
        b.name?.toLowerCase().includes(q) ||
        b.bot_id?.toLowerCase().includes(q) ||
        (b.description && String(b.description).toLowerCase().includes(q))
    );
  }, [availableBots, search]);

  const columns: ColumnsType<Bot> = useMemo(
    () => [
      {
        title: 'Bot',
        key: 'name',
        render: (_, b) => (
          <Space align="start">
            <span className="agent-bot-modal-row-ic" aria-hidden>
              <RobotOutlined />
            </span>
            <div>
              <div className="agent-bot-modal-name">{b.name}</div>
              <Text type="secondary" className="agent-bot-modal-slug">
                {b.bot_id}
              </Text>
            </div>
          </Space>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 110,
        render: (status: string) => (
          <Tag color={status === 'published' ? 'success' : 'default'}>{status || '—'}</Tag>
        ),
      },
      {
        title: 'Details',
        key: 'desc',
        ellipsis: true,
        render: (_, b) => (
          <span className="agent-bot-modal-desc">{b.description || '—'}</span>
        ),
      },
    ],
    []
  );

  const navigateAfterPick = useCallback(() => {
    const target = useAgentSelectedBotStore.getState().pendingReturnPath;
    clearPendingReturnPath();
    if (target && target.startsWith('/') && !target.startsWith('//')) {
      navigate(target, { replace: true });
    }
  }, [navigate, clearPendingReturnPath]);

  const onConfirm = useCallback(() => {
    const b = availableBots.find((x) => x.id === highlightId);
    if (!b) return;
    setSelection(pickFromBot(b));
    navigateAfterPick();
  }, [availableBots, highlightId, setSelection, navigateAfterPick]);

  const onRowPick = useCallback(
    (b: Bot) => {
      setSelection(pickFromBot(b));
      navigateAfterPick();
    },
    [setSelection, navigateAfterPick]
  );

  useEffect(() => {
    if (!pickerOpen) {
      setSearch('');
      setHighlightId(null);
    }
  }, [pickerOpen]);

  if (!isAgent) return null;

  const confirmDisabled = !highlightId || !availableBots.some((b) => b.id === highlightId);

  return (
    <Modal
      open={pickerOpen}
      onCancel={() => {
        closePicker();
        clearPendingReturnPath();
      }}
      maskClosable
      keyboard
      closable
      width={880}
      className="agent-bot-workspace-modal"
      title="Select workspace bot"
      footer={
        <div className="agent-bot-workspace-modal-footer">
          <Button
            onClick={() => {
              closePicker();
              clearPendingReturnPath();
            }}
          >
            Close
          </Button>
          <Button type="primary" disabled={confirmDisabled} onClick={onConfirm}>
            Use selected bot
          </Button>
        </div>
      }
      destroyOnClose={false}
    >
      <Paragraph type="secondary" className="agent-bot-workspace-modal-lead">
        Chat log and Live Chat are scoped to one bot. Your last choice is saved in local storage on this
        browser. If you close this without choosing and move to another page, the first available bot is applied
        automatically.
      </Paragraph>
      <Input
        allowClear
        size="large"
        placeholder="Search by name, id, or description…"
        prefix={<SearchOutlined className="agent-bot-workspace-modal-search-ic" />}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="agent-bot-workspace-modal-search"
      />
      {loadingBots ? (
        <div className="agent-bot-workspace-modal-loading">Loading bots…</div>
      ) : availableBots.length === 0 ? (
        <Empty className="agent-bot-workspace-modal-empty" description="No bots assigned to your account." />
      ) : (
        <Table<Bot>
          size="middle"
          rowKey="id"
          className="agent-bot-workspace-modal-table"
          pagination={{ pageSize: 8, showSizeChanger: false, hideOnSinglePage: true }}
          dataSource={filteredBots}
          columns={columns}
          rowClassName={(record) => (record.id === highlightId ? 'agent-bot-modal-row--active' : '')}
          onRow={(record) => ({
            onClick: () => setHighlightId(record.id),
            onDoubleClick: () => onRowPick(record),
          })}
        />
      )}
      {pendingReturnPath ? (
        <Text type="secondary" className="agent-bot-workspace-modal-pending">
          After you confirm, you will return to: <code>{pendingReturnPath}</code>
        </Text>
      ) : null}
    </Modal>
  );
}
